import ITask from './ITask';
import {
    Alpha,
    UIPopIn,
    OverlayFadeIn,
    OverlayFadeOut,
} from '../../System/Animation/Clip';
import { AwaitIterators, PlayAnimation, StartAnimationClip } from '../../System/Animation/AnimationHelper';
import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import { RenderSystemEvent } from '../../System/Render/RenderSystem';
import OverlayComponent from '../../Component/UI/Overlay';
import FarmSystem from 'System/Farm/FarmSystem';
import Game from 'Game';
import FarmUISystem from 'System/UI/FarmUISystem';
import Arrow from 'Prototype/Level/Arrow';
import Point from 'Utility/Point';
import TransformComponent from 'Component/Transform';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { PlayerActions } from 'State/Player/PlayerActions';
import FarmMapHandler from 'System/Farm/Handler/FarmMapHandler';
import Interactable from 'Component/Interactable';
import { FarmFTUXLayout } from 'Layouts/Farm/FarmFTUXLayout';
import TextComponent from 'Component/Render/Text';
import CleanupManager from 'Utility/CleanupManager';
import SpriteComponent from 'Component/Render/Sprite';
import Renderable from 'Component/Renderable';
import { PlaySound } from 'System/Audio/AudioHelper';

export default class FarmFTUXTask implements ITask<FarmSystem> {

    public *execute(system: FarmSystem): IterableIterator<any> {
        let nextScreen: boolean = false;
        // Overlay setup
        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, system.animator);
        const overlay = system.animator.getChildByName('overlay');
        const queueNextFTUXStep = () => { nextScreen = true; }
        overlay.once(GameObjectEvent.SELECTED, queueNextFTUXStep);

        // Henry initial setup
        let layout = new FarmFTUXLayout();;;

        system.animator.addChild(layout.root);

        // Get all objects and components needed
        const stepCleanupManager = new CleanupManager();
        const dialogTextComponent = layout.dialog_text.getComponent(TextComponent);
        const dialogTransformComponent = layout.dialog.getComponent(TransformComponent);
        const dialogTailTransformComponent = layout.dialog_tail.getComponent(TransformComponent);

        const uiSystem = system.scene.getSystem(FarmUISystem);
        const farmHudHandler = uiSystem.hud;
        const constructionButton = farmHudHandler.constructionButton;
        const constructionButtonParent = constructionButton.parent;

        const arrow: Arrow = GameObject.Instantiate(Arrow, {
            name: 'farm_arrow',
            position: new Point(0, -60),
            texture: 'farm_arrow',
            meta: {
                speed: 400,
                offset: new Point(0, 20)
            },
        });

        const farmMapHandler = uiSystem.map;

        const ftuxSteps: (() => IterableIterator<any>)[] = [
            showInitialDialogOverlay,
            promptEnableConstructionMode,
            promptClickOnCropSlot,
            promptSelectAndConfirmItem,
            promptSlotAreaPurchase
        ]

        // Play each step
        for (let i = 0; i < ftuxSteps.length; i++) {
            // clear interaction from previous step
            nextScreen = false;

            yield* ftuxSteps[i]();

            // Wait for user interaction
            while (!nextScreen) {
                yield;
            }

            stepCleanupManager.cleanUpAll();
        }

        yield* AwaitIterators([
            PlayAnimation(new OverlayFadeOut(overlay)),
            fadeOut(layout.root),
        ]);
        layout.root.destroy();


        // Enable interaction with farm map
        overlay.getComponent(OverlayComponent).preventInteraction = false;

        PlayerActions.setFTUXUnlocks([FTUXType.FARM]);
        return;

        function *showInitialDialogOverlay(): IterableIterator<any> {
            const timeout = Game.ticker.setTimeout(() => {
                StartAnimationClip(new UIPopIn(layout.continueText, { duration: 750 }))
            }, 5000);

            stepCleanupManager.add(() => {
                Game.ticker.clearTimeout(timeout);
                layout.continueText.getComponent(Renderable).visible = false;
            });
            // Prevent interaction with farm map
            overlay.getComponent(OverlayComponent).preventInteraction = true;

            // Fade in overlay
            StartAnimationClip(new OverlayFadeIn(overlay));
        }

        // ***** Show second FTUX screen *****
        // ***** Click on construction button *****
        function *promptEnableConstructionMode(): IterableIterator<any> {
            //overlay.getComponent(OverlayComponent).preventInteraction = false;
            yield* fadeOut(layout.root);

            // Change to next ftux text
            dialogTextComponent.text = Game.locale.getText('FarmFTUX3');

            // Get construction button, add to overlay
            constructionButton.once(GameObjectEvent.SELECTED, queueNextFTUXStep);
            constructionButton.getComponent(Interactable).toggle();

            // Add arrow to construction button
            constructionButton.addChild(arrow);
            layout.root.addChild(constructionButton);

            yield* fadeIn(layout.root);
        }

        // ***** Show third FTUX screen *****
        // ***** Click on the shown crop slot *****
        function *promptClickOnCropSlot(): IterableIterator<any> {
            yield* fadeOut(layout.root);

            // Add construction button back to his parent and toggle back off
            constructionButton.getComponent(Interactable).toggle(false);
            constructionButtonParent.addChild(constructionButton);

            // Change to next ftux text
            dialogTextComponent.text = Game.locale.getText('FarmFTUX4');

            // Set next henry texture and fix height and width
            const henryImageComponent = layout.henry.getComponent(SpriteComponent)
            henryImageComponent.texture = 'ftux_henry';
            henryImageComponent.width = 253;
            henryImageComponent.height = 336;

            // Move dialog bubble
            dialogTransformComponent.position.y = -275;

            // Add slot to overlay - it is invisible at this point but required for interactivity.
            const cropSlot = FarmMapHandler.getSlot('crop_1');
            const cropSlotParent = cropSlot.parent;
            layout.root.addChild(cropSlot);


            // Add slot icons to overlay.
            const iconParent = cropSlot.layout.taskIcon.parent;
            layout.root.addChild(cropSlot.layout.taskIcon);
            layout.root.addChild(cropSlot.layout.replyTaskIcon);

            // Move arrow to crop slot and fix position
            cropSlot.addChild(arrow);
            const arrowTransform = arrow.getComponent(TransformComponent)
            arrowTransform.position.set(-30, 50);
            arrowTransform.rotation = (5 * Math.PI) / 4; // 225º

            stepCleanupManager.add(() => {
                // Move slot back to original parent.
                cropSlotParent.addChild(cropSlot);

                // Move icons back to original parent.
                iconParent.addChild(cropSlot.layout.taskIcon);
                iconParent.addChild(cropSlot.layout.replyTaskIcon);
            });
            cropSlot.interactable.gameObject.once(GameObjectEvent.SELECTED, queueNextFTUXStep);

            yield* fadeIn(layout.root);
        }

        // ***** Show fourth FTUX screen *****
        // ***** Select crop in the UI selection and confirm *****
        function *promptSelectAndConfirmItem(): IterableIterator<any> {
            yield* fadeOut(layout.root);

            // Add henry to UI item selection
            const farmUISystem = system.scene.getSystem(FarmUISystem);
            farmUISystem.overlayPopup.parent.addChild(layout.root);
            
            // Change to next ftux text
            dialogTextComponent.text = Game.locale.getText('FarmFTUX5');

            // Destroy arrow object
            arrow.destroy();

            // Move dialog bubble
            dialogTransformComponent.position.set(-220, -450);

            // Move dialog tail
            dialogTailTransformComponent.position.x = 65;

            system.scene.once('NextFarmFTUX', queueNextFTUXStep);

            yield* fadeIn(layout.root);
        }

        // ***** Show fifth FTUX screen *****
        // ***** Shows slot areas to buy, click anywhere to finish the ftux *****
        function *promptSlotAreaPurchase(): IterableIterator<any> {
            yield* fadeOut(layout.root);

            // Change to next ftux text
            dialogTextComponent.text = Game.locale.getText('FarmFTUX7');

            // Fix X of henry
            const henryTransformComponent = layout.henry.getComponent(TransformComponent);
            henryTransformComponent.position.x -= 300;

            // Add signs to overlay
            const farmLayout = farmMapHandler.getLayout() as any;
            const signs = farmLayout.areaContainer.area.map((area:any) => {
                const sign = area.saleSign;
                sign.setDisplayParent(layout.root);
                return sign
            });

            // Remove signs when step is complete
            stepCleanupManager.add(() => {
                signs.forEach((sign:any) => {
                    sign.removeDisplayParent();
                });
            })

            overlay.once(GameObjectEvent.SELECTED, queueNextFTUXStep);

            yield* fadeIn(layout.root);
        }

        function *fadeOut(object: GameObject): IterableIterator<any> {
            yield* PlayAnimation(
                new Alpha(object, {
                    startAlpha: 1,
                    endAlpha: 0,
                    duration: 300,
                })
            );
        }

        function *fadeIn(object: GameObject): IterableIterator<any> {
            PlaySound('speech_bubble');
            yield* PlayAnimation(
                new Alpha(object, {
                    startAlpha: 0,
                    endAlpha: 1,
                    duration: 300,
                })
            );
        }
    }
}
